import * as React from 'react';

import { cn } from '../../../lib/utils';
import { Skeleton } from '../../ui/skeleton';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface KpiCardSkeletonProps {
  /** Additional classes merged onto the root shell — e.g. a min-height to stretch this into a larger panel placeholder for future dashboard rows. Defaults keep today's KPI-card footprint unchanged. */
  className?: string;
  /** Renders a third bar matching the real `KpiCard` subtitle's footprint (`mt-1.5` + xs height) — set on slots whose real card will render a `subtitle`, so no layout shift occurs once it's swapped in. */
  withSubtitle?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Loading placeholder for `KpiCard` — mirrors its exact layout footprint (bordered card shell,
 * `flex min-w-0 items-start gap-3 p-4` inner, and an optional subtitle bar via `withSubtitle`) so
 * no layout shift occurs once real cards replace it. Presentational only. Accepts an optional
 * `className` so future, larger dashboard rows can reuse the same shell at a different size.
 */
export function KpiCardSkeleton({ className, withSubtitle }: KpiCardSkeletonProps): React.JSX.Element {
  return (
    <div className={cn('rounded-lg border bg-card text-card-foreground shadow-sm', className)}>
      <div className="flex min-w-0 items-start gap-3 p-4">
        <Skeleton className="mt-0.5 h-9 w-9 rounded-lg" />
        <div className="min-w-0 flex-1">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-2 h-6 w-14" />
          {withSubtitle && <Skeleton className="mt-1.5 h-3 w-24" />}
        </div>
      </div>
    </div>
  );
}
